import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Play, Save, ArrowLeft, Trash2, Loader2, Sparkles, ListPlus, History } from "lucide-react";
import { QueryHistoryTab } from "./QueryHistoryTab";
import { useQueryBuilder } from "@/hooks/queries";
import { useSavedQuery, useCreateSavedQuery, useUpdateSavedQuery, useTrackQueryExecution } from "@/hooks/savedQueries";
import { useAuth } from "@/providers/AuthProvider";
import { useToast } from "@/hooks/use-toast";
import { ConditionGroup } from "./ConditionGroup";
import { QueryPreview } from "./QueryPreview";
import { ResultsTable } from "./ResultsTable";
import { SaveQueryDialog } from "./SaveQueryDialog";
import { NaturalLanguageQueryDialog } from "./NaturalLanguageQueryDialog";
import { AddToListDialog } from "./AddToListDialog";
import { QueryDefinition } from "@/types/savedQuery";
import { QueryGroup } from "@/types/queryBuilder";
import { generateQueryPreview } from "@/utils/queryBuilder";
import { FavoriteButton } from "@/components/favorites/FavoriteButton";
import { useEntityHistory } from "@/hooks/useEntityHistory";

export function QueryDetailPage() {
	const { t } = useTranslation();
	const { toast } = useToast();
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { user } = useAuth();
	const isNew = id === 'new';

	const [isSaveOpen, setIsSaveOpen] = useState(false);
	const [isAIOpen, setIsAIOpen] = useState(false);
	const [isAddToListOpen, setIsAddToListOpen] = useState(false);
	const [hasLoadedQuery, setHasLoadedQuery] = useState(false);
	const [shouldAutoRun, setShouldAutoRun] = useState(false);
	const [selectedCandidateIds, setSelectedCandidateIds] = useState<string[]>([]);

	// Fetch existing query if we have an ID
	const { data: savedQuery, isLoading: isLoadingQuery } = useSavedQuery(isNew ? undefined : id);
	const { trackVisit } = useEntityHistory();

	// Reset state when query ID changes
	useEffect(() => {
		setHasLoadedQuery(false);
		setShouldAutoRun(false);
		setSelectedCandidateIds([]);
	}, [id]);

	// Track visit when query is loaded
	useEffect(() => {
		if (savedQuery && !isNew) {
			trackVisit('query', savedQuery.id, savedQuery.name);
		}
	}, [savedQuery, isNew, trackVisit]);

	const {
		rootGroup,
		results,
		resultsCount,
		isLoading,
		isCountLoading,
		hasRun,
		addCondition,
		updateCondition,
		removeCondition,
		addGroup,
		updateGroupType,
		removeGroup,
		runQuery,
		loadQuery,
		clearQuery,
		getQueryDefinition
	} = useQueryBuilder();

	const createSavedQuery = useCreateSavedQuery();
	const updateSavedQuery = useUpdateSavedQuery();
	const trackExecution = useTrackQueryExecution();

	// Load the query when data is fetched
	useEffect(() => {
		if (savedQuery && !hasLoadedQuery) {
			const def = savedQuery.query_definition as any;
			if (def?.rootGroup) {
				loadQuery(def.rootGroup as QueryGroup);
				setHasLoadedQuery(true);
				setShouldAutoRun(true);
			}
		}
	}, [savedQuery, hasLoadedQuery, loadQuery]);

	// Auto-run the query after loading
	useEffect(() => {
		if (shouldAutoRun && hasLoadedQuery) {
			setShouldAutoRun(false);
			runQuery();
		}
	}, [shouldAutoRun, hasLoadedQuery, runQuery]);

	// Track execution when viewing an existing query
	useEffect(() => {
		if (savedQuery && !isNew) {
			trackExecution.mutate(savedQuery.id);
		}
	}, [savedQuery?.id]);

	const handleSave = async (name: string, description?: string, isPublic?: boolean) => {
		const queryDef = getQueryDefinition();

		if (isNew) {
			const newQuery = await createSavedQuery.mutateAsync({
				name,
				description,
				query_definition: queryDef as unknown as QueryDefinition,
				is_public: isPublic
			});
			setIsSaveOpen(false);
			// Navigate to the new query's URL
			navigate(`/queries/${newQuery.id}`, { replace: true });
		} else if (id) {
			await updateSavedQuery.mutateAsync({
				id,
				updates: {
					name,
					description,
					query_definition: queryDef as unknown as QueryDefinition,
					is_public: isPublic
				}
			});
			setIsSaveOpen(false);
		}
	};

	const handleRun = async () => {
		setSelectedCandidateIds([]);
		await runQuery();
	};

	const handleAIQueryGenerated = (query: QueryGroup) => {
		loadQuery(query);
		toast({
			title: t("queries.ai.loaded", "AI Query loaded"),
			description: t("queries.ai.loadedDescription", "Your query has been generated. Click Run to see results."),
		});
	};

	const handleBack = () => {
		navigate('/queries');
	};

	// Loading state for existing query
	if (!isNew && isLoadingQuery)
		return (
			<div className="container mx-auto py-6 space-y-6">
				<Skeleton className="h-10 w-48" />
				<Skeleton className="h-64 w-full" />
				<Skeleton className="h-96 w-full" />
			</div>
		);

	const isOwner = isNew || savedQuery?.created_by === user?.id;
	const queryName = savedQuery?.name || t("queries.new.title", "New Query");

	return (
		<div className="container mx-auto py-6 space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-4">
					<Button variant="ghost" size="sm" onClick={handleBack}>
						<ArrowLeft className="h-4 w-4 mr-2" />
						{t("common.back", "Back")}
					</Button>
					<div>
						<h1 className="text-2xl font-bold">{queryName}</h1>
						{savedQuery?.description && (
							<p className="text-sm text-muted-foreground">{savedQuery.description}</p>
						)}
					</div>
				</div>
				<div className="flex items-center gap-2">
					{savedQuery && <FavoriteButton entityType="query" entityId={savedQuery.id} />}
					<Button
						variant="outline"
						onClick={() => setIsAIOpen(true)}
						className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border-purple-300 hover:border-purple-400 hover:bg-gradient-to-r hover:from-purple-500/20 hover:to-pink-500/20"
					>
						<Sparkles className="h-4 w-4 mr-2 text-purple-500" />
						{t("queries.actions.aiQuery", "AI Query")}
					</Button>
				</div>
			</div>

			{/* Query Builder Card */}
			<Card>
				<CardHeader className="pb-3">
					<CardTitle className="text-lg">
						{t("queries.builder.title", "Query Builder")}
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<ConditionGroup
						group={rootGroup}
						isRoot
						onTypeChange={(type) => updateGroupType(rootGroup.id, type)}
						onAddCondition={() => addCondition(rootGroup.id)}
						onAddGroup={(type) => addGroup(rootGroup.id, type)}
						onUpdateCondition={updateCondition}
						onRemoveCondition={removeCondition}
						onUpdateGroupType={updateGroupType}
						onRemoveGroup={removeGroup}
						onAddConditionToGroup={addCondition}
						onAddGroupToGroup={addGroup}
					/>

					<Separator />

					{/* Query Preview */}
					<div className="space-y-2">
						<h4 className="text-sm font-medium text-muted-foreground">
							{t("queries.preview.title", "Query Preview")}
						</h4>
						<QueryPreview
							group={rootGroup}
							count={resultsCount}
							isLoading={isCountLoading}
							hasRun={hasRun}
						/>
					</div>

					<Separator />

					{/* Action Buttons */}
					<div className="flex flex-wrap gap-2">
						<Button onClick={handleRun} disabled={isLoading}>
							{isLoading ? (
								<Loader2 className="h-4 w-4 mr-2 animate-spin" />
							) : (
								<Play className="h-4 w-4 mr-2" />
							)}
							{t("queries.actions.run", "Run Query")}
						</Button>

						{isOwner && (
							<Button variant="outline" onClick={() => setIsSaveOpen(true)}>
								<Save className="h-4 w-4 mr-2" />
								{isNew ? t("queries.actions.save", "Save Query") : t("queries.actions.update", "Update Query")}
							</Button>
						)}

						<Button variant="ghost" onClick={clearQuery}>
							<Trash2 className="h-4 w-4 mr-2" />
							{t("queries.actions.clear", "Clear All")}
						</Button>
					</div>
				</CardContent>
			</Card>

			{/* Results Card */}
			<Card>
				<CardHeader className="pb-3">
					<CardTitle className="text-lg flex items-center justify-between">
						<div className="flex items-center gap-3">
							<span>{t("queries.results.title", "Results")}</span>
							{hasRun && !isLoading && (
								<span className="text-sm font-normal text-muted-foreground">
									{t("queries.results.count", "{{count}} candidates", { count: resultsCount })}
								</span>
							)}
						</div>
						{selectedCandidateIds.length > 0 && (
							<Button
								size="sm"
								variant="outline"
								onClick={() => setIsAddToListOpen(true)}
							>
								<ListPlus className="h-4 w-4 mr-2" />
								{t("queries.results.addToList", "Add {{count}} to List", {
									count: selectedCandidateIds.length
								})}
							</Button>
						)}
					</CardTitle>
				</CardHeader>
				<CardContent>
					<ResultsTable
						results={results}
						isLoading={isLoading}
						hasRun={hasRun}
						selectedIds={selectedCandidateIds}
						onSelectionChange={setSelectedCandidateIds}
					/>
				</CardContent>
			</Card>

			{/* Dialogs */}
			<SaveQueryDialog
				open={isSaveOpen}
				onOpenChange={setIsSaveOpen}
				onSave={handleSave}
				isLoading={createSavedQuery.isPending || updateSavedQuery.isPending}
				queryPreview={generateQueryPreview(rootGroup)}
				initialName={savedQuery?.name}
				initialDescription={savedQuery?.description}
				initialIsPublic={savedQuery?.is_public}
				isUpdate={!isNew}
			/>

			<NaturalLanguageQueryDialog
				open={isAIOpen}
				onOpenChange={setIsAIOpen}
				onQueryGenerated={handleAIQueryGenerated}
			/>

			<AddToListDialog
				open={isAddToListOpen}
				onOpenChange={setIsAddToListOpen}
				candidateIds={selectedCandidateIds}
				onSuccess={() => setSelectedCandidateIds([])}
			/>

			{/* History Card - Only show for existing queries */}
			{!isNew && savedQuery && (
				<Card>
					<CardHeader className="pb-3">
						<CardTitle className="text-lg flex items-center gap-2">
							<History className="h-5 w-5" />
							{t("queries.history.title", "Query History")}
						</CardTitle>
					</CardHeader>
					<CardContent>
						<QueryHistoryTab queryId={savedQuery.id} />
					</CardContent>
				</Card>
			)}
		</div>
	);
}
